import {useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import Chat from "./chat";
import {
    setWorkspaceGraphChats,
    removeWorkspaceChat,
    upsertWorkspaceChat,
    upsertWorkspaceChatParticipant,
} from "../../system/workspace-graph-slice";
import {
    normalizeMembershipEntity,
    prepareMembershipPayload,
} from "../workspace-membership/workspace-membership-identity";

// chats whose «mark read» request is on its way (shared by every screen that marks a chat read)
const readsInFlight = new Map();

export const chatPopulate = {
    project: true,
    // `team` is not populated: nothing reads it, and a role without «find» on teams made the whole messages page fail with
    // «Invalid key team» (boss's bug report 2026-10-29)
    users: {populate: {member: {populate: {profileImage: true}}}},
    createdByUser: {populate: {member: {populate: {profileImage: true}}}},
    participants: {populate: {
        user: {populate: {member: {populate: {profileImage: true}}}},
    }},
    last_chat_message: {populate: {
        user: {populate: {member: {populate: {profileImage: true}}}},
        attachment: true,
        parent: {populate: ["attachment"]},
    }},
};

export function useChat() {
    const dispatch = useDispatch();
    const workspaceMembership = useSelector((state) => state.app?.workspaceMembershipData);
    const memberships = workspaceMembership?.workspace?.workspace_memberships || [];
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const run = async (action) => {
        setLoading(true);
        setError(null);
        try {
            return await action();
        } catch (requestError) {
            setError(requestError);
            throw requestError;
        } finally {
            setLoading(false);
        }
    };

    const getChats = async () => run(async () => {
        const response = await new Chat().getAll({
            populate: chatPopulate,
            sort: ["lastMessageAt:desc", "updatedAt:desc"],
            pagination: {pageSize: 100},
        });
        const items = (response?.data || []).map(normalizeChat);
        dispatch(setWorkspaceGraphChats(items));
        return items;
    });

    const getChat = async (documentId) => run(async () => {
        try {
            const response = await new Chat().getById(documentId, {populate: chatPopulate});
            const item = normalizeChat(response?.data);
            if (item) dispatch(upsertWorkspaceChat(item));
            return item;
        } catch (requestError) {
            if ([403, 404].includes(requestError?.status)) return null;
            throw requestError;
        }
    });

    const getProjectChat = async (projectDocumentId) => run(async () => {
        const ensured = await new Chat().action({}, `/project-chats/${projectDocumentId}`);
        const identifier = ensured?.data?.documentId || ensured?.data?.id;
        const response = identifier
            ? await new Chat().getById(identifier, {populate: chatPopulate})
            : ensured;
        const item = normalizeChat(response?.data);
        if (item) dispatch(upsertWorkspaceChat(item));
        return item;
    });

    const getTeamChat = async (teamDocumentId) => run(async () => {
        const ensured = await new Chat().action({}, `/team-chats/${teamDocumentId}`);
        const identifier = ensured?.data?.documentId || ensured?.data?.id;
        const response = identifier
            ? await new Chat().getById(identifier, {populate: chatPopulate})
            : ensured;
        const item = normalizeChat(response?.data);
        if (item) dispatch(upsertWorkspaceChat(item));
        return item;
    });

    const createChat = async (data) => run(async () => {
        const payload = prepareMembershipPayload("chat", data, memberships, {
            currentMembership: workspaceMembership,
            operation: "create",
        });
        const response = await new Chat().create(payload, {populate: chatPopulate});
        const item = normalizeChat(response?.data);
        if (item) dispatch(upsertWorkspaceChat(item));
        return item;
    });

    const updateChat = async (documentId, data) => run(async () => {
        const payload = prepareMembershipPayload("chat", data, memberships, {
            currentMembership: workspaceMembership,
            operation: "update",
        });
        const response = await new Chat().update(documentId, payload, {populate: chatPopulate});
        const item = normalizeChat(response?.data);
        if (item) dispatch(upsertWorkspaceChat(item));
        return item;
    });

    // «Mark read» is background bookkeeping: opening a chat used to send it twice at once (the open effect and the
    // unread effect), racing each other on the same row. One request per chat at a time now, and a failure is logged
    // by the caller instead of showing «خطایی در سرور رخ داد…» (boss's bug report 2026-11-02).
    const markChatRead = async (documentId) => {
        const key = String(documentId);
        if (!readsInFlight.has(key)) {
            readsInFlight.set(key, (async () => {
                const response = await new Chat().putAction(`/chats/${documentId}/read`, {}, {}, {suppressGlobalErrorHandler: true});
                const participant = normalizeMembershipEntity(response?.data, "chatParticipant", memberships);
                if (participant) dispatch(upsertWorkspaceChatParticipant(participant));
                return participant;
            })().finally(() => readsInFlight.delete(key)));
        }
        return readsInFlight.get(key);
    };

    const deleteChat = async (item) => run(async () => {
        const documentId = item?.documentId ?? item?.id ?? item;
        await new Chat().delete(documentId);
        dispatch(removeWorkspaceChat(typeof item === "object" ? item : {documentId}));
        return true;
    });

    function normalizeChat(item) {
        const normalized = normalizeMembershipEntity(item, "chat", memberships);
        if (!normalized) return normalized;
        normalized.participants = (normalized.participants || []).map((participant) =>
            normalizeMembershipEntity(participant, "chatParticipant", memberships));
        normalized.last_chat_message = normalizeMembershipEntity(
            normalized.last_chat_message,
            "chatMessage",
            memberships,
        );
        return normalized;
    }

    return {getChats, getChat, getProjectChat, getTeamChat, createChat, updateChat, deleteChat, markChatRead, loading, error};
}
